import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { validateLogin } from '@/lib/authValidation';
import { friendlyError } from '@/lib/friendlyError';
import { isAccountExistsError } from '@/lib/accountExists';
import { trackAuthStep } from '@/lib/authFunnel';
import { useRateLimit } from '@/hooks/useRateLimit';
import { AuthForm } from '@/components/auth/AuthForm';
import { PremiumBackground } from '@/components/auth/PremiumBackground';
import { supabase } from '@/integrations/supabase/client';
import { AnimatePresence } from 'framer-motion';

// Sem tela de boas-vindas. Spotify, Netflix, Uber e Notion abrem direto no
// campo; nenhum deles pede um toque antes de ela poder digitar. Restam duas
// A tela de pedir link por email saiu em 20/09/2026 (motivo no AuthForm), e com
// ela a unica view alternativa. O estado fica como está para o dia em que uma
// segunda view voltar a existir.
type AuthView = 'form';

/**
 * O erro que volta de signIn/signUp vem em formas diferentes conforme a versao
 * do GoTrue: as vezes traz `code`, as vezes so `status`, as vezes so texto.
 * Este shape le os tres sem `any` espalhado pelo handler.
 */
type AuthAttemptError = {
  message?: string;
  code?: string;
  status?: number;
};

const Auth = memo(function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // Nao ha mais "modo": o formulario e um so. Este flag agora significa apenas
  // "ja descobrimos que existe conta com este email", o que muda o texto de
  // ajuda e realca o Forgot password.
  const [accountExists, setAccountExists] = useState(false);
  const [view, setView] = useState<AuthView>('form');
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();

  // Rate limit login attempts (5 per minute)
  const loginRateLimit = useRateLimit(5, 60000);

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Funil antes da conta (23/09/2026). Ver src/lib/authFunnel.ts: nada digitado sai daqui.
  const formStarted = useRef(false);
  useEffect(() => {
    if (!user) trackAuthStep('auth', 'shown');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoized callbacks for child components
  const handleEmailChange = useCallback((value: string) => {
    if (!formStarted.current) { formStarted.current = true; trackAuthStep('auth', 'form_started'); }
    setEmail(value);
    setAccountExists(false);
  }, []);

  const handlePasswordChange = useCallback((value: string) => {
    if (!formStarted.current) { formStarted.current = true; trackAuthStep('auth', 'form_started'); }
    setPassword(value);
  }, []);

  const handleOpenForm = useCallback(() => {
    setAccountExists(false);
    setView('form');
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginRateLimit.canMakeCall()) {
      const remainingMs = loginRateLimit.getRemainingTime();
      const secondsLeft = Math.ceil(remainingMs / 1000);
      trackAuthStep('auth', 'rate_limited');
      toast.error(`Too many attempts. Please wait ${secondsLeft} seconds.`, {
        duration: 4000,
      });
      return;
    }

    setLoading(true);

    try {
      const validationResult = validateLogin(email, password);

      if (validationResult.ok === false) {
        trackAuthStep('auth', 'validation_error', { detail: !email.includes('@') ? 'email' : !password ? 'password_empty' : 'other' });
        toast.error(validationResult.message, { duration: 4000 });
        setLoading(false);
        return;
      }

      // ── Caminho unico (17/09/2026) ────────────────────────────────────────
      // Ela nao escolhe mais entre "criar conta" e "entrar". O app tenta entrar
      // e, se nao houver conta, cria — a mesma senha serve para os dois casos.
      //
      // Porque: 310 pessoas em 30 dias apertaram "Get Started" ja tendo conta,
      // 132 nunca entraram, 123 sumiram sem escrever. O patch de 16/09 detectava
      // o erro e virava o formulario, o que funciona mas trata o sintoma: a
      // escolha errada continuava sendo oferecida. Aqui ela deixa de existir.
      //
      // Descartado: perguntar ao backend "este email tem conta?" antes da senha
      // (identifier-first classico). Exigiria um endpoint que confirma, para
      // qualquer anonimo, se um email e cliente — e o password-reset-code desta
      // casa foi escrito de proposito para NAO responder isso. Este desenho
      // entrega o mesmo ganho sem abrir essa porta.

      // userId pode vir indefinido: quando o GoTrue esta configurado para exigir
      // confirmacao de email, o signUp volta sem erro e sem sessao. Ler `.id`
      // as cegas ali lancaria TypeError DEPOIS da conta ja ter sido criada — a
      // mae veria um erro generico numa conta que existe. Sem id, manda para o
      // quiz, que e o destino de quem acabou de criar conta de qualquer forma.
      const goIn = async (userId: string | undefined, isNew: boolean) => {
        trackAuthStep('auth', isNew ? 'signup_ok' : 'signin_ok', { userId });
        loginRateLimit.reset();
        setAccountExists(false);

        if (isNew) {
          toast.success("Welcome! Let's get you set up", { duration: 3000 });
          navigate('/quiz', { replace: true });
          return;
        }

        if (!userId) {
          navigate('/', { replace: true });
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('quiz_completed')
          .eq('id', userId)
          .single();

        toast.success('Welcome back!', { duration: 3000 });
        navigate(profile?.quiz_completed ? '/' : '/quiz', { replace: true });
      };

      trackAuthStep('auth', 'submit');
      // 1) Tenta entrar.
      const signInAttempt = await signIn(email, password);

      if (!signInAttempt.error) {
        await goIn(signInAttempt.user?.id, false);
        return;
      }

      const signInError = signInAttempt.error as AuthAttemptError;
      const signInMessage = signInError.message || '';

      // Erro que nao e de credencial (rede, 500, acesso bloqueado) para aqui:
      // tentar criar conta por cima de um timeout so confunde.
      const isCredentialMiss =
        /invalid\s+login|invalid\s+credentials|invalid\s+email\s+or\s+password/i.test(signInMessage) ||
        signInError.status === 400;

      if (!isCredentialMiss) {
        trackAuthStep('auth', /500|EOF|Connection|internet|fetch/i.test(signInMessage) ? 'network_error' : 'other_error', {
          detail: signInError.status ? `signin_http_${signInError.status}` : 'signin',
        });
        if (signInMessage.includes('500') || signInMessage.includes('EOF')) {
          toast.error('Connection issue. Please try again.', { duration: 4000 });
        } else if (signInMessage.includes('Connection failed') || signInMessage.includes('internet')) {
          toast.error('Please check your internet connection and try again.', { duration: 5000 });
        } else if (signInMessage.includes('restricted') || signInMessage.includes('Access')) {
          // Mensagem nossa, escrita para ela: passa inteira.
          toast.error(signInMessage, { duration: 6000 });
        } else {
          toast.error(friendlyError(signInMessage), { duration: 5000 });
        }
        return;
      }

      // 2) Nao entrou por credencial. Pode ser conta que ainda nao existe.
      const signUpAttempt = await signUp(email, password);

      if (!signUpAttempt.error) {
        await goIn(signUpAttempt.user?.id, true);
        return;
      }

      const signUpError = signUpAttempt.error as AuthAttemptError;
      const signUpMessage = signUpError.message || '';
      const signUpCode = signUpError.code || '';
      const signUpStatus = signUpError.status;

      // GoTrue reporta "ja existe" em varias formas conforme a versao: code ou texto.
      // O status 422 sozinho NAO (e tambem email invalido e senha fraca): 23/09.
      const accountAlreadyThere = isAccountExistsError({ code: signUpCode, status: signUpStatus, message: signUpMessage });

      if (accountAlreadyThere) {
        // A conta existe e o signIn falhou: a senha e que esta errada. Dizer
        // isso com todas as letras, e apontar a saida — nao "email ou senha
        // invalidos", que deixa ela sem saber qual dos dois corrigir.
        trackAuthStep('auth', 'password_mismatch');
        setAccountExists(true);
        setPassword('');
        toast.error('That password does not match', {
          description: 'You already have an account with this email. Use "Forgot password?" below to get a 6 digit code.',
          duration: 7000,
        });
        return;
      }

      if (signUpCode === 'EMAIL_NOT_APPROVED' || signUpMessage.includes('Purchase required')) {
        // Nao e tentativa de credencial: devolver a vaga do rate limit.
        loginRateLimit.release();
        trackAuthStep('auth', 'email_not_approved');
        toast.error('We could not find a purchase for this email', {
          description: 'Use the same email address you paid with.',
          duration: 8000,
        });
        return;
      }

      if (/at least \d+ characters/i.test(signUpMessage)) {
        trackAuthStep('auth', 'validation_error', { detail: 'password_short' });
        toast.error('Choose a password with at least 6 characters', { duration: 5000 });
        return;
      }

      trackAuthStep('auth', 'other_error', { detail: signUpCode || (signUpStatus ? `signup_http_${signUpStatus}` : 'signup') });
      toast.error(friendlyError(signUpMessage), { duration: 5000 });
    } catch (error: unknown) {
      trackAuthStep('auth', 'exception');
      toast.error(friendlyError(error), { duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [email, password, signIn, signUp, navigate, loginRateLimit]);

  return (
    // A tela segue o tema do app, sem forcar nada.
    //
    // Eu tinha forcado `dark` aqui argumentando "a entrada e escura sempre, e a
    // identidade do anuncio". Era opiniao: a base diz o contrario. Medido em
    // 17/09/2026 sobre profiles.theme — 5.654 pessoas em light (85,2%), 155 em
    // dark (2,3%), 827 sem escolha salva. E o proprio ThemeProvider manda quem
    // chega pela primeira vez para o claro.
    //
    // Forcar preto na porta faria a mae entrar numa tela escura e cair num app
    // branco — o mesmo descasamento que este trabalho existia para consertar,
    // so que ao contrario.
    //
    // (O CLAUDE.md descreve dark como tema primario. A base escolheu outra
    // coisa: de quem salvou preferencia, 97,3% salvou light.)
    //
    // O safe-area e aplicado uma vez so, dentro de cada view: antes havia
    // padding aqui E la, e em tela baixa o dobro empurrava o rodape para fora.
    <div
      className="fixed inset-0 flex flex-col overflow-hidden bg-background"
      // O override do acento que vivia aqui foi APAGADO em 20/09/2026, como o
      // proprio comentario dele previa: "o dia em que o app inteiro unificar o
      // laranja basta apagar esta linha". O token agora tem um valor por tema
      // (#FF7A1A no escuro, #C2410C no claro) e esta tela pega o do claro, que
      // e onde ela sempre renderiza. Ver src/index.css.
    >
      {/* Premium background layer */}
      <PremiumBackground />

      {/* Content layer with AnimatePresence for smooth transitions */}
      <div
        className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <AnimatePresence mode="wait">
          {view === 'form' && (
            <AuthForm
              key="form"
              email={email}
              password={password}
              loading={loading}
              accountExists={accountExists}
              onEmailChange={handleEmailChange}
              onPasswordChange={handlePasswordChange}
              onSubmit={handleSubmit}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

export default Auth;
